/**
 * Where the pending-agent-task count shows in a module's nav. Module navs call their agent page "Agent Activity"
 * (/crm/agent-activity, /billing/agent-activity, /assets/agent-activity, /hr/agent-activity), usually as a dropdown
 * child, so matching only a top-level label "Agents" (the old rule) never put the badge anywhere. A nav item labelled
 * "Agents" still gets it.
 */
import type { NavItem } from '../../modules/types';

/** @returns true for the nav item that leads to the module's agent work */
export function isAgentNavItem(item: NavItem): boolean {
  return item.label === 'Agents' || /\/agent-activity$/.test(item.path);
}

/**
 * Puts the count on the module's agent nav item, top level or one dropdown level down (the nav supports one level).
 * Children are checked first: a dropdown's own path is its first child's, so the count lands on the row.
 * @param items - The module's nav items
 * @param count - Pending agent tasks for the module
 * @returns The items, with the badge on the agent item (unchanged when count is 0 or nothing matches)
 */
export function withAgentTaskBadge(items: NavItem[], count: number): NavItem[] {
  if (count <= 0) return items;
  return items.map((item) => {
    if (item.children?.some(isAgentNavItem)) {
      return { ...item, children: item.children.map((c) => (isAgentNavItem(c) ? { ...c, badge: count } : c)) };
    }
    if (isAgentNavItem(item)) return { ...item, badge: count };
    return item;
  });
}

/**
 * Puts `count` on the row that goes to `path`, top level or one dropdown level down. Like withAgentTaskBadge, a
 * dropdown's rows are checked first: a heading's own path is often its first row's, and the nav never shows a
 * heading's own badge (it sums its rows), so the count must land on the row.
 * @param items - The module's nav items
 * @param path - The path of the row that shows the count
 * @param count - The count (nothing changes when it is 0)
 * @returns The items, with the badge on that row
 */
export function withBadgeAt(items: NavItem[], path: string, count: number): NavItem[] {
  if (count <= 0) return items;
  return items.map((item) => {
    if (item.children?.some((c) => c.path === path)) {
      return { ...item, children: item.children.map((c) => (c.path === path ? { ...c, badge: count } : c)) };
    }
    return !item.children && item.path === path ? { ...item, badge: count } : item;
  });
}

/**
 * The count a dropdown shows on its closed heading: the sum of its rows' badges. A heading's own `badge` was never
 * shown (the P2 "Quotes" mention count is set there) and is still not, so this PR changes no other badge.
 */
export function groupBadge(item: NavItem): number {
  return (item.children ?? []).reduce((sum, c) => sum + (c.badge ?? 0), 0);
}
